// frontend/modules/internal-account/views/InternalAccountManagementView.tsx
import React from 'react';
import { Card, Typography, Button, Input, Space, Modal, Select, Row, Col } from 'antd';
import { PlusOutlined, ReloadOutlined, ExclamationCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import { useInternalAccounts } from '../hooks/useInternalAccounts';
import { InternalAccountDTO } from '../stores/internal-account-store';
import InternalAccountList from '../components/InternalAccountList';

const { Title } = Typography;
const { Search } = Input;
const { confirm } = Modal;
const { Option } = Select;

const InternalAccountManagementView: React.FC = () => {
    const { dark } = useTheme();
    const navigate = useNavigate();
    const {
        filteredAccounts,
        isLoading,
        searchTerm,
        branches,
        branchFilter,
        statusFilter,
        
        setSearchTerm,
        closeAccount,
        loadAccounts,
        filterByBranch,
        filterByStatus,
        resetFilters
    } = useInternalAccounts();

    // Handle account creation
    const handleCreateAccount = () => {
        navigate('/accounts/internal/new');
    };

    // Handle account editing
    const handleEditAccount = (account: InternalAccountDTO) => {
        navigate(`/accounts/internal/edit/${account.branch}/${account.glNo}/${account.acNo}`);
    };

    // Handle account closing
    const handleCloseAccount = (account: InternalAccountDTO) => {
        confirm({
            title: 'Are you sure you want to close this account?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action will set the account status to Closed.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                closeAccount(account);
            }
        });
    };

    return (
        <Card className={`${dark ? 'bg-gray-800 text-white' : 'bg-white'} shadow-md`}>
            <div className="flex justify-between items-center mb-4">
                <Title level={4} className={dark ? 'text-white' : ''}>
                    Internal Account Management
                </Title>
                <Space>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={handleCreateAccount}
                    >
                        Create Account
                    </Button>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={() => loadAccounts()}
                    >
                        Refresh
                    </Button>
                </Space>
            </div>
            
            <Row gutter={16} className="mb-4">
                <Col span={8}>
                    <Search
                        placeholder="Search by account name, GL number, or account number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col span={5}>
                    <Select
                        placeholder="Filter by Branch"
                        value={branchFilter}
                        onChange={filterByBranch}
                        allowClear
                        style={{ width: '100%' }}
                    >
                        {branches.map(branch => (
                            <Option key={branch.branch} value={branch.branch}>
                                {branch.branch} ({branch.brName})
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={5}>
                    <Select
                        placeholder="Filter by Status"
                        value={statusFilter}
                        onChange={filterByStatus}
                        allowClear
                        style={{ width: '100%' }}
                    >
                        <Option value="A">Active</Option>
                        <Option value="C">Closed</Option>
                    </Select>
                </Col>
                <Col span={6}>
                    <Button 
                        icon={<FilterOutlined />} 
                        onClick={resetFilters}
                    >
                        Reset Filters
                    </Button>
                </Col>
            </Row>
            
            <InternalAccountList 
                accounts={filteredAccounts}
                isLoading={isLoading}
                onEdit={handleEditAccount}
                onClose={handleCloseAccount}
            />
        </Card>
    );
};

export default InternalAccountManagementView;
